import React, {
  memo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { Button } from 'react-bootstrap';
import { ChromePicker } from 'react-color';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeDropper } from '@fortawesome/pro-regular-svg-icons';
import classnames from 'classnames';

import style from './style.module.scss';

/** Trim, collapse duplicate leading # (e.g. paste onto existing #), then use a single # prefix. */
const hexInputToValue = (raw) => {
  const trimmed = String(raw).trim();
  return `#${trimmed.replaceAll('#', '')}`;
};

const ColorPicker = ({
  title,
  colorName,
  value,
  onChange,
}) => {
  const [showPicker, setShowPicker] = useState(false);

  const togglePicker = useCallback((evt) => {
    if (!evt.target.closest('.chrome-picker')) {
      setShowPicker(!showPicker);
    }
  }, [showPicker]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (showPicker) {
      document.body.addEventListener('click', togglePicker);
      return () => {
        document.body.removeEventListener('click', togglePicker);
      };
    }

    document.body.removeEventListener('click', togglePicker);
  }, [showPicker]);

  const handlePickerChange = (pickedColor) => {
    // to be able to re-use this component with RHFInput
    // value property needs to be the first argument
    onChange(pickedColor.hex, colorName);
  };

  const handleInputChange = useCallback(({ target }) => {
    onChange(hexInputToValue(target.value), colorName);
  }, [onChange, colorName]);

  const handleScreenPick = useCallback(async (evt) => {
    evt.stopPropagation();
    if (typeof window === 'undefined' || !window.EyeDropper) {
      return;
    }
    try {
      const eyeDropper = new window.EyeDropper();
      const { sRGBHex } = await eyeDropper.open();
      onChange(sRGBHex, colorName);
    } catch (err) {
      if (err && err.name !== 'AbortError') {
        // User cancel is AbortError; other failures are rare (permissions, etc.)
      }
    }
  }, [onChange, colorName]);

  const supportsScreenPick = typeof window !== 'undefined' && window.EyeDropper;

  const rootClass = classnames(
    style.colorBlock,
    'p-3',
    'mb-2',
    'border',
    'd-flex',
    'justify-content-between',
    'align-items-center',
  );

  return (
    <div className={rootClass}>
      <span>{title}</span>
      <div>
        <span className="mr-1">Hex:</span>

        <input
          placeholder="Add hex code"
          className={style.input}
          type="text"
          value={value}
          onChange={(handleInputChange)}
        />

        <Button
          style={{ backgroundColor: value }}
          className={classnames('ml-2', style.colorBtn)}
          onClick={togglePicker}
        />

        {supportsScreenPick && (
          <button
            type="button"
            className={classnames('ml-2', style.screenPickIcon)}
            title="Pick color from screen"
            aria-label="Pick color from screen"
            onClick={handleScreenPick}
          >
            <FontAwesomeIcon icon={faEyeDropper} />
          </button>
        )}

        {showPicker && (
          <ChromePicker
            disableAlpha
            className={style.colorPicker}
            color={value}
            onChange={handlePickerChange}
          />
        )}
      </div>
    </div>
  );
};

export default memo(ColorPicker);
